import { useApolloClient } from '@apollo/client';
import { compact, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  OrganizationFieldsFragment,
  PromoCodeFieldsFragment,
  PromoCodeType,
  PromoCodeValueQueryDocument,
} from 'src/gqlReactTypings.generated.d';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useScopedReducer } from 'src/shef-global-state/shefGlobalState';
import { IPromotionals } from './promoCodeTypes';
import { saveStateToStorage } from './promotionalsState';

export const usePromotionalsHook = () => {
  const { state, reduce } = useScopedReducer('promotionals');
  const apolloClient = useApolloClient();

  const location = useLocation();
  const isOnMarketplaceCheckout = locationInRoutes(location, [Routes.CONSUMER_ORDER_FINALIZE]);

  const setOrganization = useCallback(
    (org: OrganizationFieldsFragment | null) => {
      reduce((state) =>
        saveStateToStorage({
          ...state,
          organization: org,
        })
      );
    },
    [reduce]
  );

  const setCode = useCallback(
    (code: PromoCodeFieldsFragment | null) => {
      reduce((state) =>
        saveStateToStorage({
          ...state,
          availablePromos: uniqBy(compact([...(state.availablePromos ?? []), code]), 'id'),
          code,
          // Clear the flag when a code is set (allows auto-apply on next page load)
          userExplicitlyCleared: false,
        })
      );
    },
    [reduce]
  );

  const setError = useCallback(
    (error: IPromotionals['error']) => {
      reduce((state) => ({
        ...state,
        error,
      }));
    },
    [reduce]
  );

  const code = useMemo(
    () => (state.organization ? state.organization.promoCode : state.code ?? null),
    [state.organization, state.code]
  );

  const promoText = useMemo(() => {
    switch (code?.promoCodeType) {
      case PromoCodeType.Referral:
        return 'Referral Promo';
      case PromoCodeType.OrganizationInternal:
        return state.organization?.name ? `${state.organization.name} Promo` : 'Company Promo';
      case PromoCodeType.GiftCard:
        return 'Gift card';
      case undefined:
        return null;
      default:
        return 'Promo';
    }
  }, [code, state.organization]);

  const unsetActiveCode = useCallback(() => {
    reduce((state) =>
      saveStateToStorage({
        ...state,
        code: null,
        error: null,
        organization: null,
      })
    );
  }, [reduce]);

  // Mark that the user explicitly cleared the code (prevents auto-reapplication)
  const markUserExplicitlyCleared = useCallback(() => {
    reduce((state) =>
      saveStateToStorage({
        ...state,
        userExplicitlyCleared: true,
      })
    );
  }, [reduce]);

  // Remove a particular promo code
  const removeCode = useCallback(
    (codeToRemove: PromoCodeFieldsFragment) => {
      const newAvailablePromos = state.availablePromos.filter((promoCode) => promoCode.code !== codeToRemove.code);
      const removingActivecode = code?.code === codeToRemove.code;

      reduce((state) =>
        saveStateToStorage({
          availablePromos: newAvailablePromos,
          code: removingActivecode ? newAvailablePromos[0] ?? null : code ?? null,
          error: removingActivecode ? null : state.error,
          organization: removingActivecode ? null : state.organization,
        })
      );
    },
    [code, reduce, state.availablePromos]
  );

  // Removes all saved promos from storage
  const clearState = useCallback(() => {
    reduce(() =>
      saveStateToStorage({
        availablePromos: [],
        code: null,
        error: null,
        organization: null,
      })
    );
  }, [reduce]);

  const revalidatePromoCodes = useCallback(async () => {
    const availablePromos = state.availablePromos ?? [];
    if (availablePromos.length === 0) return;

    const validatedPromos: PromoCodeFieldsFragment[] = [];

    for (const promo of availablePromos) {
      try {
        const { data } = await apolloClient.query({
          query: PromoCodeValueQueryDocument,
          variables: {
            code: promo.code ?? '',
            isMealPlanCode: !isOnMarketplaceCheckout,
          },
          fetchPolicy: 'network-only',
        });

        if (data?.promoCodeValue?.code && !data?.promoCodeValue?.error) {
          validatedPromos.push(promo);
        }
      } catch (error) {
        console.error(`Error validating promo code ${promo.code}:`, error);
      }
    }

    // Update the state with only valid promo codes
    reduce((state) =>
      saveStateToStorage({
        ...state,
        availablePromos: validatedPromos,
        code: code && validatedPromos.find((p) => p.code === code.code) ? code : validatedPromos[0] ?? null,
      })
    );
  }, [apolloClient, state.availablePromos, code, isOnMarketplaceCheckout, reduce]);

  return {
    // The availablePromos ?? [] in theory should be unnecessary;
    // it seemed to be occasionally nullish though. Maybe
    // from old clients from before availablePromos was
    // introduced.
    availablePromos: state.availablePromos ?? [],
    code,
    promoText,
    organization: state.organization,
    setCode,
    setError,
    error: state.error,
    setOrganization,
    unsetActiveCode,
    removeCode,
    clearState,
    revalidatePromoCodes,
    userExplicitlyCleared: state.userExplicitlyCleared ?? false,
    markUserExplicitlyCleared,
  };
};
